import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeftRight, Sparkles } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { MealRow } from "@/components/MealRow";
import { MobileShell } from "@/components/MobileShell";
import { PlannerMacroStrip } from "@/components/PlannerMacroStrip";
import { PlannerSharePanel } from "@/components/PlannerSharePanel";
import { RecipeDrawer } from "@/components/RecipeDrawer";
import { ShareHistoryPanel } from "@/components/ShareHistoryPanel";
import { SwapSuggestionSheet } from "@/components/SwapSuggestionSheet";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { api, getApiError } from "@/lib/api";
import { isPremium } from "@/lib/entitlements";
import { findActiveMacroPreset } from "@/lib/macros";
import { buildPlannerShareText, buildRecipeShareText, copyTextToClipboard, getShareSuccessMessage, shareContent } from "@/lib/share";
import { cn } from "@/lib/utils";


const weekdayLabels = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const contextLabelMap = { busy: "busy", normal: "normal", "low-prep": "low-prep" };
const contextLabelStyles = {
  busy: "bg-rose-100 text-rose-700",
  normal: "bg-stone-100 text-stone-700",
  "low-prep": "bg-amber-100 text-amber-800",
};
const contextHints = {
  busy: "Drukke dag: snelle maaltijden en leftovers krijgen meer gewicht bij volgende suggesties.",
  normal: "Normale dag: Smartly houdt de bestaande balans aan.",
  "low-prep": "Low-prep dag: recepten met weinig voorbereiding krijgen meer voorrang.",
};
const manualContextPresets = {
  busy: { day_busyness_score: "busy", cooking_time_available_min: 25, lunch_context: "onderweg", dinner_context: "thuis" },
  normal: { day_busyness_score: "normal", cooking_time_available_min: 25, lunch_context: "thuis", dinner_context: "thuis" },
  "low-prep": { day_busyness_score: "normal", cooking_time_available_min: 15, lunch_context: "thuis", dinner_context: "thuis" },
};
const calendarProviderLabels = {
  google_calendar: "Google Calendar",
  apple_calendar: "Apple Calendar",
};


export default function PlannerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const touchStartRef = useRef(null);
  const [planner, setPlanner] = useState(null);
  const [activeDay, setActiveDay] = useState(0);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedMealItem, setSelectedMealItem] = useState(null);
  const [contextForm, setContextForm] = useState(null);
  const [presetName, setPresetName] = useState("");
  const [presets, setPresets] = useState([]);
  const [presetDrafts, setPresetDrafts] = useState({});
  const [weekdayDefaults, setWeekdayDefaults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importValue, setImportValue] = useState("");
  const [sharedPreview, setSharedPreview] = useState(null);
  const [shareHistory, setShareHistory] = useState({ created_shares: [], templates: [], recent_activity: [] });
  const [templateBusyId, setTemplateBusyId] = useState(null);
  const [swapOptions, setSwapOptions] = useState(null);
  const [macroPresets, setMacroPresets] = useState([]);
  const [calendarConnection, setCalendarConnection] = useState(null);

  const sharedWeekmenuId = searchParams.get("share");
  const weekStartParam = searchParams.get("week_start");
  const daySearchParam = searchParams.get("day");
  const dayParam = daySearchParam === null ? null : Number(daySearchParam);

  const resolveActiveDay = useCallback((responseData) => {
    if (Number.isInteger(dayParam) && dayParam >= 0 && dayParam < (responseData?.days?.length || 0)) {
      return dayParam;
    }
    return responseData.today_index || 0;
  }, [dayParam]);

  useEffect(() => {
    const loadPlannerData = async () => {
      const [plannerResponse, presetsResponse, weekdayDefaultsResponse, shareHistoryResponse, macroPresetsResponse, calendarConnectionResponse] = await Promise.all([
        api.get("/planner", {
          params: {
            week_start: weekStartParam || undefined,
          },
        }),
        api.get("/planner/context-presets"),
        api.get("/planner/context-presets/defaults"),
        api.get("/planner/shares/history"),
        api.get("/profile/macro-presets"),
        api.get("/planner/calendar-connection"),
      ]);
      setPlanner(plannerResponse.data);
      setActiveDay(resolveActiveDay(plannerResponse.data));
      setPresets(presetsResponse.data);
      setWeekdayDefaults(weekdayDefaultsResponse.data);
      setShareHistory(shareHistoryResponse.data);
      setMacroPresets(macroPresetsResponse.data);
      setCalendarConnection(calendarConnectionResponse.data);
    };

    loadPlannerData();
  }, [dayParam, daySearchParam, resolveActiveDay, weekStartParam]);

  useEffect(() => {
    if (!sharedWeekmenuId) {
      setSharedPreview(null);
      return;
    }

    const loadSharedPreview = async () => {
      try {
        const response = await api.get(`/planner/shares/${sharedWeekmenuId}`);
        setSharedPreview(response.data);
        setImportValue((current) => (current.trim() ? current : window.location.href));
      } catch {
        setSharedPreview(null);
      }
    };

    loadSharedPreview();
  }, [sharedWeekmenuId]);

  useEffect(() => {
    setPresetDrafts(
      Object.fromEntries(
        presets.filter((preset) => !preset.is_system).map((preset) => [preset.id, preset.name]),
      ),
    );
  }, [presets]);

  const day = planner?.days?.[activeDay] || null;
  const dayMacroSummary = day && planner
    ? {
        ...day,
        kcal_target: planner.daily_calorie_target || planner.calorie_target_daily || 0,
        carb_target: planner.daily_carb_target || 0,
      }
    : null;

  // Premium gate: backend supplies the canonical view; fall back to client
  // helper for first render before /api/planner resolves.
  const premiumGate = planner?.premium_gate || { is_premium: isPremium(user), free_day_count: 3, locked_after_index: 2 };
  const lockedAfterIndex = premiumGate.is_premium ? null : premiumGate.locked_after_index;
  const isDayLocked = (index) => lockedAfterIndex !== null && index > lockedAfterIndex;
  const isActiveDayLocked = isDayLocked(activeDay);
  const activeMacroPreset = findActiveMacroPreset(macroPresets, {
    calorie_target_daily: planner?.daily_calorie_target,
    daily_carb_target: planner?.daily_carb_target,
    max_carbs_per_meal: planner?.max_carbs_per_meal,
    carb_priority_mode: planner?.carb_priority_mode,
  });

  useEffect(() => {
    if (!day?.context) {
      return;
    }
    setContextForm(day.context);
  }, [day]);

  const updatePlanner = async (request, successMessage = "Planner bijgewerkt.") => {
    try {
      setBusy(true);
      const response = await request();
      setPlanner(response.data);
      setActiveDay(resolveActiveDay(response.data));
      toast.success(successMessage);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const openRecipe = async (mealItem) => {
    try {
      const response = await api.get(`/recipes/${mealItem.recipe.id}`);
      setSelectedMealItem(mealItem);
      setSelectedRecipe(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  const openSwapSuggestions = async (itemId) => {
    try {
      setBusy(true);
      const response = await api.get(`/planner/items/${itemId}/swap-options`);
      setSwapOptions(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const applySwapSuggestion = async (recipeId) => {
    if (!swapOptions?.item_id) {
      return;
    }

    await updatePlanner(
      () => api.post(`/planner/items/${swapOptions.item_id}/swap`, { recipe_id: recipeId }),
      "Carb-bewuste swap toegepast.",
    );
    setSwapOptions(null);
  };

  const moveDay = (direction) => {
    setActiveDay((current) => {
      const nextDay = current + direction;
      if (!planner?.days?.[nextDay]) {
        return current;
      }
      return nextDay;
    });
  };

  const handleTouchStart = (event) => {
    touchStartRef.current = event.changedTouches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    const endX = event.changedTouches[0].clientX;
    const delta = endX - (touchStartRef.current || endX);

    if (Math.abs(delta) < 48) {
      return;
    }

    moveDay(delta > 0 ? -1 : 1);
  };

  const applyDayContext = async () => {
    if (!contextForm) {
      return;
    }
    await updatePlanner(
      () => api.post("/planner/context/manual", { ...contextForm, date: day?.date_iso, manual_override: true, context_source: "manual", calendar_provider: calendarConnection?.selected_provider || null }),
      "Dagcontext opgeslagen. Nieuwe ranking geldt bij volgende generate of swap.",
    );
  };

  const applyPreset = async (preset) => {
    const nextContext = {
      day_busyness_score: preset.day_busyness_score,
      cooking_time_available_min: preset.cooking_time_available_min,
      lunch_context: preset.lunch_context,
      dinner_context: preset.dinner_context,
      date: day?.date_iso,
      manual_override: true,
      context_source: "manual",
      calendar_provider: calendarConnection?.selected_provider || null,
    };
    setContextForm(nextContext);
    await updatePlanner(() => api.post("/planner/context/manual", nextContext), `${preset.name} opgeslagen voor deze dag.`);
  };

  const applyManualContextLabel = (label) => {
    const preset = manualContextPresets[label];
    if (!preset) {
      return;
    }
    setContextForm((current) => ({
      ...current,
      ...preset,
      date: day?.date_iso,
      manual_override: true,
      context_source: "manual",
      calendar_provider: calendarConnection?.selected_provider || null,
    }));
  };

  const connectCalendarPlaceholder = async (provider) => {
    try {
      const response = await api.post("/planner/calendar-connection", { provider });
      setCalendarConnection(response.data);
      setContextForm((current) => current ? ({ ...current, calendar_provider: provider }) : current);
      const providerRow = response.data?.providers?.find((item) => item.provider === provider);
      toast.success(provider === "google_calendar" ? `${calendarProviderLabels[provider]} staat nu op ${providerRow?.status_label || "Ready to connect"}.` : `${calendarProviderLabels[provider]} blijft een placeholder seam.`);
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  const saveCurrentAsPreset = async () => {
    if (!contextForm || !presetName.trim()) {
      return;
    }

    try {
      await api.post("/planner/context-presets", {
        name: presetName.trim(),
        day_busyness_score: contextForm.day_busyness_score,
        cooking_time_available_min: contextForm.cooking_time_available_min,
        lunch_context: contextForm.lunch_context,
        dinner_context: contextForm.dinner_context,
      });
      setPresetName("");
      const [presetsResponse, weekdayDefaultsResponse] = await Promise.all([
        api.get("/planner/context-presets"),
        api.get("/planner/context-presets/defaults"),
      ]);
      setPresets(presetsResponse.data);
      setWeekdayDefaults(weekdayDefaultsResponse.data);
      toast.success("Preset opgeslagen.");
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  const renamePreset = async (presetId) => {
    const preset = presets.find((current) => current.id === presetId);
    const nextName = presetDrafts[presetId]?.trim();
    if (!preset || !nextName) {
      return;
    }

    try {
      await api.put(`/planner/context-presets/${presetId}`, {
        name: nextName,
        day_busyness_score: preset.day_busyness_score,
        cooking_time_available_min: preset.cooking_time_available_min,
        lunch_context: preset.lunch_context,
        dinner_context: preset.dinner_context,
      });
      const [presetsResponse, weekdayDefaultsResponse] = await Promise.all([
        api.get("/planner/context-presets"),
        api.get("/planner/context-presets/defaults"),
      ]);
      setPresets(presetsResponse.data);
      setWeekdayDefaults(weekdayDefaultsResponse.data);
      toast.success("Preset hernoemd.");
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  const deletePreset = async (presetId) => {
    try {
      const response = await api.delete(`/planner/context-presets/${presetId}`);
      setPresets(response.data);
      const weekdayDefaultsResponse = await api.get("/planner/context-presets/defaults");
      setWeekdayDefaults(weekdayDefaultsResponse.data);
      toast.success("Preset verwijderd.");
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  const saveWeekdayDefault = async (weekdayIndex, presetId) => {
    try {
      const response = await api.post("/planner/context-presets/defaults", {
        weekday_index: weekdayIndex,
        preset_id: presetId === "none" ? null : presetId,
      });
      setWeekdayDefaults(response.data);
      const plannerResponse = await api.get("/planner", {
        params: {
          week_start: weekStartParam || undefined,
        },
      });
      setPlanner(plannerResponse.data);
      setActiveDay(resolveActiveDay(plannerResponse.data));
      toast.success("Weekdag-default opgeslagen.");
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  const sharePlanner = async () => {
    if (!planner) {
      return;
    }

    try {
      setShareBusy(true);
      const exportResponse = await api.post("/planner/shares/export", {
        week_start: planner.week_start,
        day_index: activeDay,
      });
      const shareUrl = `${window.location.origin}/planner?share=${exportResponse.data.share_id}&day=${activeDay}`;
      const mode = await shareContent({
        title: `Smartly weekmenu ${planner.week_start}`,
        text: buildPlannerShareText(planner),
        url: shareUrl,
      });
      toast.success(getShareSuccessMessage(mode, "Weekmenu"));
      const shareHistoryResponse = await api.get("/planner/shares/history");
      setShareHistory(shareHistoryResponse.data);
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      toast.error("Weekmenu delen lukte niet.");
    } finally {
      setShareBusy(false);
    }
  };

  const copyPlannerJson = async () => {
    if (!planner) {
      return;
    }

    try {
      setShareBusy(true);
      const exportResponse = await api.post("/planner/shares/export", {
        week_start: planner.week_start,
        day_index: activeDay,
      });
      await copyTextToClipboard(exportResponse.data.payload_json);
      toast.success("Weekmenu JSON gekopieerd naar klembord.");
    } catch {
      toast.error("Weekmenu JSON kopiëren lukte niet.");
    } finally {
      setShareBusy(false);
    }
  };

  const importWeekmenu = async (source = importValue) => {
    if (!source.trim()) {
      return;
    }

    try {
      setImportBusy(true);
      const response = await api.post("/planner/shares/import", {
        source: source.trim(),
        week_start: planner?.week_start,
      });
      setPlanner(response.data);
      setActiveDay(resolveActiveDay(response.data));
      setImportValue("");
      setSharedPreview(null);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete("share");
        return next;
      });
      const shareHistoryResponse = await api.get("/planner/shares/history");
      setShareHistory(shareHistoryResponse.data);
      toast.success("Gedeeld weekmenu geïmporteerd.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setImportBusy(false);
    }
  };

  const saveTemplateFromPreview = async () => {
    try {
      setImportBusy(true);
      await api.post("/planner/shares/template", {
        source: window.location.href,
      });
      const shareHistoryResponse = await api.get("/planner/shares/history");
      setShareHistory(shareHistoryResponse.data);
      toast.success("Gedeeld weekmenu opgeslagen als template.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setImportBusy(false);
    }
  };

  const applyTemplate = async (templateId) => {
    try {
      setTemplateBusyId(templateId);
      const response = await api.post(`/planner/templates/${templateId}/apply`, {
        week_start: planner?.week_start,
      });
      setPlanner(response.data);
      setActiveDay(resolveActiveDay(response.data));
      const shareHistoryResponse = await api.get("/planner/shares/history");
      setShareHistory(shareHistoryResponse.data);
      toast.success("Template in planner gezet.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setTemplateBusyId(null);
    }
  };

  const shareRecipe = async () => {
    if (!selectedRecipe) {
      return;
    }

    try {
      const mode = await shareContent({
        title: selectedRecipe.title,
        text: buildRecipeShareText(selectedRecipe),
        url: `${window.location.origin}/recipes?recipe=${selectedRecipe.slug || selectedRecipe.id}`,
      });
      toast.success(getShareSuccessMessage(mode, "Recept"));
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      toast.error("Recept delen lukte niet.");
    }
  };

  return (
    <>
      <MobileShell
        title="Weekplanner"
        eyebrow="Één actieve dag tegelijk"
        action={<Button className="rounded-full bg-emerald-700 px-4 text-white hover:bg-emerald-800" onClick={() => updatePlanner(() => api.post("/planner/generate", {}))} disabled={busy} data-testid="planner-generate-button">Genereer</Button>}
      >
        <div className="space-y-4">
          <section className="sticky top-4 z-20 -mx-1 rounded-[28px] border border-stone-200 bg-[#fbf7ef]/90 p-3 backdrop-blur" data-testid="planner-sticky-header">
            <div className="grid grid-cols-7 gap-2" data-testid="planner-day-tabs">
              {planner?.days?.map((currentDay, index) => {
                const locked = isDayLocked(index);
                return (
                  <button
                    key={currentDay.date_iso}
                    type="button"
                    onClick={() => setActiveDay(index)}
                    className={cn(
                      "relative rounded-[18px] px-1 py-3 text-center transition-all duration-200",
                      activeDay === index ? "bg-emerald-700 text-white" : "bg-white text-stone-600",
                      locked && activeDay !== index ? "opacity-60" : "",
                    )}
                    data-testid={`planner-day-tab-${index}`}
                    data-locked={locked ? "true" : "false"}
                  >
                    <span className="block text-xs font-semibold uppercase tracking-[0.24em]">{currentDay.label}</span>
                    <span className="mt-1 block text-xs" data-testid={`planner-day-date-${index}`}>{currentDay.date_iso.slice(8, 10)}</span>
                    {locked ? (
                      <span className="absolute right-1 top-1 text-[10px]" data-testid={`planner-day-tab-lock-${index}`} aria-label="Premium vereist">
                        🔒
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="rounded-[28px] border border-stone-200 bg-[linear-gradient(135deg,_rgba(243,230,198,0.9),_rgba(236,245,234,0.95))] p-4" data-testid="planner-active-day-card">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="planner-active-day-kicker">
                  Actieve dag
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-stone-900" data-testid="planner-active-day-title">
                  {day?.label} {day?.date_iso}
                </h2>
                <p className="mt-2 text-sm text-stone-600" data-testid="planner-active-day-carb-summary">
                  {day?.carb_total || 0} g carbs vandaag · doel {planner?.daily_carb_target || 0} g
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="planner-active-day-context-pills">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${contextLabelStyles[day?.context?.context_label || "normal"]}`} data-testid="planner-active-context-label-pill">
                    {contextLabelMap[day?.context?.context_label || "normal"]}
                  </span>
                  <span className="rounded-full bg-white/85 px-3 py-1 text-xs font-medium text-stone-600" data-testid="planner-active-context-source-pill">
                    {day?.context?.manual_override ? "manual override" : day?.context?.context_source === "calendar_import" ? "agenda import" : "default"}
                  </span>
                </div>
                <p className="mt-2 text-sm text-stone-600" data-testid="planner-active-context-hint">
                  {contextHints[day?.context?.context_label || "normal"]}
                </p>
              </div>
              <div className="rounded-full bg-white/85 px-3 py-2 text-xs font-medium text-stone-700" data-testid="planner-day-hint">
                Swipe links/rechts
              </div>
            </div>
          </section>

          <div className="inline-flex w-fit items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800" data-testid="planner-active-macro-preset-pill">
            Actieve macro preset: <span className="ml-1 font-semibold" data-testid="planner-active-macro-preset-pill-label">{activeMacroPreset?.name || "Handmatig"}</span>
          </div>

          {dayMacroSummary ? <PlannerMacroStrip day={dayMacroSummary} /> : null}

          <PlannerSharePanel
            activeDayLabel={day?.label || "Weekoverzicht"}
            importValue={importValue}
            onImportValueChange={setImportValue}
            onImportWeekmenu={() => importWeekmenu()}
            onPreviewImport={() => importWeekmenu(window.location.href)}
            onSaveTemplate={saveTemplateFromPreview}
            onShareLink={sharePlanner}
            onCopyJson={copyPlannerJson}
            preview={sharedPreview}
            shareBusy={shareBusy}
            importBusy={importBusy}
            templateBusy={importBusy}
          />

          <ShareHistoryPanel history={shareHistory} onApplyTemplate={applyTemplate} applyingTemplateId={templateBusyId} />

          <section className="rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="planner-day-context-card">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 text-emerald-700" />
              <div className="w-full">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="planner-day-context-kicker">
                  Day context scoring
                </p>
                <h3 className="mt-2 text-lg font-semibold text-stone-900" data-testid="planner-day-context-title">
                  Laat Smartly slimmer scoren op seizoen en dagritme
                </h3>
                <div className="mt-4 rounded-[22px] border border-dashed border-stone-300 bg-stone-50 p-3" data-testid="planner-calendar-placeholder-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="planner-calendar-placeholder-title">
                    Agenda sync seam
                  </p>
                  <p className="mt-2 text-sm text-stone-600" data-testid="planner-calendar-placeholder-description">
                    Nog geen live sync. Kies alvast een provider-landingsplek; handmatige dagcontext blijft nu leidend.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="planner-calendar-provider-buttons">
                    <Button variant="outline" className="rounded-full border-stone-200 bg-white" onClick={() => connectCalendarPlaceholder("google_calendar")} data-testid="planner-calendar-provider-google-button">
                      Google Calendar
                    </Button>
                    <Button variant="outline" className="rounded-full border-stone-200 bg-white" onClick={() => connectCalendarPlaceholder("apple_calendar")} data-testid="planner-calendar-provider-apple-button">
                      Apple Calendar
                    </Button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs" data-testid="planner-calendar-placeholder-status-row">
                    <span className="rounded-full bg-white px-3 py-1 text-stone-700" data-testid="planner-calendar-placeholder-status-pill">
                      {calendarConnection?.selected_provider ? `${calendarProviderLabels[calendarConnection.selected_provider]} · ${(calendarConnection.providers || []).find((provider) => provider.provider === calendarConnection.selected_provider)?.status_label || "Actief"}` : "Nog niet gekoppeld"}
                    </span>
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700" data-testid="planner-calendar-placeholder-mode-pill">
                      Manual-first
                    </span>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="planner-context-presets-group">
                  {presets.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyPreset(preset)}
                      className="rounded-full bg-stone-100 px-3 py-2 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-200"
                      data-testid={`planner-context-preset-${preset.id}`}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
                <div className="mt-4 rounded-[22px] border border-stone-200 bg-white p-3" data-testid="planner-manual-context-override-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="planner-manual-context-override-title">
                    Handmatige dagcontext
                  </p>
                  <p className="mt-2 text-sm text-stone-600" data-testid="planner-manual-context-override-description">
                    Sla eerst een simpele context op voor deze dag. Dat beïnvloedt volgende generate- en swap-suggesties zonder direct alles te herschrijven.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="planner-manual-context-labels">
                    {Object.keys(manualContextPresets).map((label) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => applyManualContextLabel(label)}
                        className={`rounded-full px-3 py-2 text-xs font-semibold ${contextLabelStyles[label]}`}
                        data-testid={`planner-manual-context-label-${label}`}
                      >
                        {contextLabelMap[label]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-4 rounded-[22px] border border-dashed border-stone-300 bg-stone-50 p-3" data-testid="planner-custom-preset-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="planner-custom-preset-title">
                    Sla huidige context op als preset
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Input value={presetName} onChange={(event) => setPresetName(event.target.value)} placeholder="Bijv. Mijn sportdag" className="h-11 rounded-2xl border-stone-200 bg-white" data-testid="planner-context-preset-name-input" />
                    <Button className="h-11 rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={saveCurrentAsPreset} data-testid="planner-context-preset-save-button">
                      Bewaar
                    </Button>
                  </div>
                  <div className="mt-3 space-y-2">
                    {presets.filter((preset) => !preset.is_system).map((preset) => (
                      <div key={`${preset.id}-manage`} className="rounded-[18px] bg-white px-3 py-3" data-testid={`planner-custom-preset-row-${preset.id}`}>
                        <div className="flex items-center gap-2">
                          <Input value={presetDrafts[preset.id] || ""} onChange={(event) => setPresetDrafts((current) => ({ ...current, [preset.id]: event.target.value }))} className="h-10 rounded-2xl border-stone-200" data-testid={`planner-custom-preset-name-${preset.id}`} />
                          <Button variant="outline" className="rounded-full border-stone-200 bg-white px-3" onClick={() => renamePreset(preset.id)} data-testid={`planner-custom-preset-rename-${preset.id}`}>
                            Hernoem
                          </Button>
                          <Button variant="outline" className="rounded-full border-red-200 bg-red-50 px-3 text-red-700 hover:bg-red-100" onClick={() => deletePreset(preset.id)} data-testid={`planner-custom-preset-delete-${preset.id}`}>
                            Verwijder
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-4 rounded-[22px] border border-dashed border-stone-300 bg-stone-50 p-3" data-testid="planner-weekday-defaults-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="planner-weekday-defaults-title">
                    Default preset per weekdag
                  </p>
                  <div className="mt-3 space-y-3">
                    {weekdayLabels.map((label, index) => (
                      <div key={label} className="flex items-center gap-3" data-testid={`planner-weekday-default-row-${index}`}>
                        <span className="w-24 text-sm text-stone-700" data-testid={`planner-weekday-default-label-${index}`}>{label}</span>
                        <Select value={weekdayDefaults.find((item) => item.weekday_index === index)?.preset_id || "none"} onValueChange={(value) => saveWeekdayDefault(index, value)}>
                          <SelectTrigger className="h-11 flex-1 rounded-2xl border-stone-200 bg-white" data-testid={`planner-weekday-default-select-${index}`}>
                            <SelectValue placeholder="Geen default" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Geen default</SelectItem>
                            {presets.map((preset) => (
                              <SelectItem key={`${index}-${preset.id}`} value={preset.id}>
                                {preset.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Tempo</label>
                    <Select value={contextForm?.day_busyness_score || "normal"} onValueChange={(value) => setContextForm((current) => ({ ...current, date: day?.date_iso, day_busyness_score: value }))}>
                      <SelectTrigger className="h-12 rounded-2xl border-stone-200" data-testid="planner-context-busyness-select">
                        <SelectValue placeholder="Tempo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="relaxed">Rustig</SelectItem>
                        <SelectItem value="normal">Normaal</SelectItem>
                        <SelectItem value="busy">Druk</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Kooktijd</label>
                    <Select value={String(contextForm?.cooking_time_available_min || 25)} onValueChange={(value) => setContextForm((current) => ({ ...current, date: day?.date_iso, cooking_time_available_min: Number(value) }))}>
                      <SelectTrigger className="h-12 rounded-2xl border-stone-200" data-testid="planner-context-time-select">
                        <SelectValue placeholder="Kooktijd" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 min</SelectItem>
                        <SelectItem value="25">25 min</SelectItem>
                        <SelectItem value="40">40 min</SelectItem>
                        <SelectItem value="55">55 min</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Lunchmodus</label>
                    <Select value={contextForm?.lunch_context || "thuis"} onValueChange={(value) => setContextForm((current) => ({ ...current, date: day?.date_iso, lunch_context: value }))}>
                      <SelectTrigger className="h-12 rounded-2xl border-stone-200" data-testid="planner-context-lunch-select">
                        <SelectValue placeholder="Lunchmodus" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="thuis">Thuis</SelectItem>
                        <SelectItem value="onderweg">Onderweg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Dinnermodus</label>
                    <Select value={contextForm?.dinner_context || "thuis"} onValueChange={(value) => setContextForm((current) => ({ ...current, date: day?.date_iso, dinner_context: value }))}>
                      <SelectTrigger className="h-12 rounded-2xl border-stone-200" data-testid="planner-context-dinner-select">
                        <SelectValue placeholder="Dinnermodus" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="thuis">Thuis</SelectItem>
                        <SelectItem value="onderweg">Onderweg</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button className="mt-4 h-12 w-full rounded-full bg-emerald-700 text-white hover:bg-emerald-800" onClick={applyDayContext} disabled={busy} data-testid="planner-context-save-button">
                  Pas context toe op deze dag
                </Button>
              </div>
            </div>
          </section>

          <div className="space-y-3" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-testid="planner-active-day-view">
            {isActiveDayLocked ? (
              <UpgradePrompt
                reason="planner-locked-day"
                title="Deze dag is alleen beschikbaar met Smartly Premium."
                description="Smartly Free ondersteunt basisplanning. Upgrade naar Premium voor volledige weekplanning, onbeperkte pantry en slimmere boodschappen."
              />
            ) : (
              day?.items?.map((item, index) => (
                <section key={item.id} className="stagger-enter space-y-2" style={{ animationDelay: `${index * 70 + 60}ms` }} data-testid={`planner-section-${item.meal_slot}`}>
                  <div className="flex items-center justify-between px-1">
                    <h3 className="text-sm font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid={`planner-section-title-${item.meal_slot}`}>
                      {item.slot_label}
                    </h3>
                    <span className="text-xs text-stone-500" data-testid={`planner-section-meta-${item.meal_slot}`}>
                      {item.recipe.prep_time_min} min prep
                    </span>
                  </div>
                  <MealRow
                    item={item}
                    onOpen={openRecipe}
                    onSwap={openSwapSuggestions}
                    onToggleLock={(itemId, isLocked) => updatePlanner(() => api.post(`/planner/items/${itemId}/lock`, { is_locked: isLocked }))}
                  />
                </section>
              ))
            )}
          </div>

          <div className="rounded-[26px] border border-dashed border-stone-300 bg-white/70 p-4" data-testid="planner-mobile-principles-card">
            <div className="flex items-start gap-3">
              <ArrowLeftRight className="mt-0.5 h-5 w-5 text-emerald-700" />
              <div>
                <p className="text-sm font-semibold text-stone-900" data-testid="planner-mobile-principles-title">
                  Compact en scanbaar op mobiel
                </p>
                <p className="mt-2 text-sm text-stone-600" data-testid="planner-mobile-principles-description">
                  Eén dag tegelijk, compacte meal rows en snelle lock/swap acties zonder dashboard-overload.
                </p>
              </div>
            </div>
          </div>
        </div>
      </MobileShell>

      <RecipeDrawer
        open={Boolean(selectedRecipe)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedRecipe(null);
            setSelectedMealItem(null);
          }
        }}
        recipe={selectedRecipe}
        onShareRecipe={shareRecipe}
        sourceHint={selectedMealItem?.source_type === "leftover" ? `${selectedMealItem.recipe.title} staat ingepland als leftover. Je hoeft deze niet opnieuw te koken.` : null}
        onStartCooking={selectedMealItem?.can_cook ? () => {
          const mealItemId = selectedMealItem.id;
          setSelectedRecipe(null);
          setSelectedMealItem(null);
          navigate(`/cook/${mealItemId}`);
        } : null}
        onGoToGrocery={() => {
          setSelectedRecipe(null);
          setSelectedMealItem(null);
          navigate("/grocery");
        }}
      />

      <SwapSuggestionSheet
        open={Boolean(swapOptions)}
        onOpenChange={(open) => !open && setSwapOptions(null)}
        currentRecipe={swapOptions?.current_recipe}
        carbTarget={swapOptions?.meal_carb_target}
        suggestions={swapOptions?.suggestions}
        onSelectSuggestion={applySwapSuggestion}
        busy={busy}
      />
    </>
  );
}